/**
 * storefront-tracker.js — anonymous + identified browsing pixel for the
 * abandoned-cart watcher's per-customer timeline.
 *
 * Captures: page_view, product_view, collection_view, search,
 * add_to_cart, begin_checkout. Anonymous via stable cookie_id stored in
 * localStorage (rc_cid, 30-day TTL). When the chat widget or contact
 * form captures an email, an `identify` event is fired so the watcher
 * can join cookie_id → email later.
 *
 * Privacy: no raw IP (server hashes), no UTM/query/fragment (server
 * strips), no PII other than what the customer explicitly typed into a
 * form. Backend has 30-day TTL.
 */
(function () {
  'use strict';

  var CFG = window.RC_TRACK_CONFIG || {};
  // Defaults derive from current host so a missing snippet doesn't silently
  // route AU traffic to api.rolacase.us (cross-eTLD+1 = HttpOnly rc_cid
  // cookie won't flow back). Snippet-set values still win when present.
  var DEFAULT_HOST_IS_US = typeof window !== 'undefined'
    && window.location
    && /\.us(?:[:/]|$)/.test(window.location.host || '');
  var STORE = CFG.store || (DEFAULT_HOST_IS_US ? 'US' : 'AU');
  var API_BASE = CFG.apiBase || (DEFAULT_HOST_IS_US ? 'https://api.rolacase.us' : 'https://api.rolacase.com');
  var API_KEY = CFG.apiKey || 'rola-dp-7f3k9x2m4p';
  var ENDPOINT = API_BASE + '/api/v1/storefront-event';

  // Stable per-browser id, persists 30 days. localStorage so it survives
  // tab close (Shopify auto-redirects across subdomains during checkout —
  // sessionStorage would lose us at exactly the moment we need to join).
  var COOKIE_KEY = 'rc_cid';
  var IDENTIFY_KEY = 'rc_email';
  var COOKIE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

  function uuid() {
    if (window.crypto && crypto.randomUUID) {
      try { return crypto.randomUUID(); } catch (_) {}
    }
    var rnd = '';
    for (var i = 0; i < 32; i++) {
      rnd += Math.floor(Math.random() * 16).toString(16);
    }
    return rnd;
  }

  function getCookieID() {
    try {
      var raw = window.localStorage.getItem(COOKIE_KEY);
      if (raw) {
        var p = JSON.parse(raw);
        if (p && p.id && p.exp && p.exp > Date.now()) {
          return p.id;
        }
      }
      var fresh = uuid();
      window.localStorage.setItem(COOKIE_KEY, JSON.stringify({ id: fresh, exp: Date.now() + COOKIE_TTL_MS }));
      return fresh;
    } catch (_) {
      // Private mode / disabled storage — fall back to in-memory id
      // for the lifetime of the page so events still group within a
      // single visit even if they don't survive a refresh.
      if (!window.__rc_cid_mem) window.__rc_cid_mem = uuid();
      return window.__rc_cid_mem;
    }
  }

  function getIdentifiedEmail() {
    try {
      return (window.localStorage.getItem(IDENTIFY_KEY) || '').trim().toLowerCase();
    } catch (_) { return ''; }
  }

  function setIdentifiedEmail(email) {
    if (!email) return;
    var trimmed = String(email).trim().toLowerCase();
    if (!trimmed) return;
    try { window.localStorage.setItem(IDENTIFY_KEY, trimmed); } catch (_) {}
    track('identify', { email: trimmed });
  }

  // Expose so other theme scripts (chat widget, contact form) can hand us
  // the email when they capture it. They already collect it; we just want
  // to know about it.
  window.RolacaseTrack = window.RolacaseTrack || {};
  window.RolacaseTrack.identify = setIdentifiedEmail;
  window.RolacaseTrack.track = function (type, payload) { track(type, payload || {}); };

  function track(eventType, payload) {
    if (!eventType) return;
    payload = payload || {};
    var body = {
      cookie_id: getCookieID(),
      email: payload.email || getIdentifiedEmail() || '',
      store: STORE,
      event_type: eventType,
      path: window.location.pathname,
      page_title: document.title || '',
      product_handle: payload.product_handle || '',
      product_title: payload.product_title || '',
      variant: payload.variant || '',
      quantity: payload.quantity || 0,
      search_query: payload.search_query || '',
      referrer: document.referrer || '',
      raw: payload.raw || {}
    };
    try {
      fetch(ENDPOINT, {
        method: 'POST',
        keepalive: true,
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY },
        body: JSON.stringify(body)
      }).catch(function () {});
    } catch (_) {}
  }

  // ===== Bootstrap: page-level events =====
  function detectPageType() {
    var path = (window.location.pathname || '').toLowerCase();
    var t = (window.RC_TRACK_PAGE && window.RC_TRACK_PAGE.type) || '';
    if (t) return t;
    if (path.indexOf('/products/') === 0) return 'product';
    if (path.indexOf('/collections/') === 0) return 'collection';
    if (path === '/search' || path.indexOf('/search?') === 0) return 'search';
    if (path === '/cart' || path === '/checkout' || path.indexOf('/checkout/') === 0) return 'checkout';
    return 'page';
  }

  function bootPageView() {
    var meta = (window.RC_TRACK_PAGE || {});
    var type = detectPageType();

    if (type === 'product') {
      track('product_view', {
        product_handle: meta.product_handle || '',
        product_title: meta.product_title || '',
        variant: meta.variant || ''
      });
    } else if (type === 'collection') {
      track('collection_view', {
        product_handle: meta.collection_handle || '',
        product_title: meta.collection_title || ''
      });
    } else if (type === 'search') {
      var qs = new URLSearchParams(window.location.search);
      track('search', { search_query: qs.get('q') || '' });
    } else if (type === 'checkout') {
      track('begin_checkout');
    } else {
      track('page_view', { product_title: meta.page_label || '' });
    }
  }

  // ===== Cart fetch interceptor =====
  // Shopify ajax-add-to-cart hits /cart/add.js. We monkey-patch fetch +
  // XHR to capture the variant + qty without depending on the theme's
  // specific cart drawer impl. Falls through to the original request
  // unchanged on every code path.
  function hookCartAdd() {
    if (window.fetch && !window.__rc_fetch_hooked) {
      var origFetch = window.fetch.bind(window);
      window.fetch = function (input, init) {
        try {
          var url = typeof input === 'string' ? input : (input && input.url) || '';
          if (url.indexOf('/cart/add') >= 0) {
            // best-effort body parse
            var bodyTxt = '';
            if (init && init.body) {
              try { bodyTxt = typeof init.body === 'string' ? init.body : ''; } catch (_) {}
            }
            var p = parseCartAddBody(bodyTxt);
            track('add_to_cart', p);
          }
        } catch (_) {}
        return origFetch(input, init);
      };
      window.__rc_fetch_hooked = true;
    }
    if (window.XMLHttpRequest && !window.__rc_xhr_hooked) {
      var XO = window.XMLHttpRequest.prototype.open;
      var XS = window.XMLHttpRequest.prototype.send;
      window.XMLHttpRequest.prototype.open = function (m, u) {
        try { this.__rc_url = u; } catch (_) {}
        return XO.apply(this, arguments);
      };
      window.XMLHttpRequest.prototype.send = function (body) {
        try {
          if (this.__rc_url && String(this.__rc_url).indexOf('/cart/add') >= 0) {
            var p = parseCartAddBody(typeof body === 'string' ? body : '');
            track('add_to_cart', p);
          }
        } catch (_) {}
        return XS.apply(this, arguments);
      };
      window.__rc_xhr_hooked = true;
    }
  }

  function parseCartAddBody(txt) {
    var out = { quantity: 1 };
    if (!txt) return out;
    try {
      if (txt.charAt(0) === '{') {
        var j = JSON.parse(txt);
        if (j.id) out.variant = String(j.id);
        if (j.quantity) out.quantity = parseInt(j.quantity, 10) || 1;
        if (j.properties) out.raw = { properties: j.properties };
      } else {
        var qs = new URLSearchParams(txt);
        if (qs.get('id')) out.variant = qs.get('id');
        if (qs.get('quantity')) out.quantity = parseInt(qs.get('quantity'), 10) || 1;
      }
    } catch (_) {}
    return out;
  }

  // ===== Rich engagement: every action, every move =====

  function safe(fn) {
    return function () {
      try { return fn.apply(this, arguments); } catch (_) {}
    };
  }

  function hookScrollDepth() {
    var milestones = [25, 50, 75, 100];
    var fired = {};
    var ticking = false;
    function compute() {
      ticking = false;
      try {
        var doc = document.documentElement;
        var bodyH = Math.max(doc.scrollHeight, document.body.scrollHeight);
        var viewport = window.innerHeight;
        var scrolled = window.scrollY + viewport;
        if (bodyH <= viewport) return;
        var pct = Math.min(100, Math.round((scrolled / bodyH) * 100));
        for (var i = 0; i < milestones.length; i++) {
          var m = milestones[i];
          if (pct >= m && !fired[m]) {
            fired[m] = true;
            track('scroll_depth', { quantity: m });
          }
        }
      } catch (_) {}
    }
    window.addEventListener('scroll', safe(function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(compute);
    }), { passive: true });
  }

  function hookTimeOnPage() {
    var milestones = [30, 60, 180, 600];
    var seconds = 0;
    var fired = {};
    var visible = !document.hidden;
    document.addEventListener('visibilitychange', safe(function () { visible = !document.hidden; }));
    setInterval(safe(function () {
      if (!visible) return;
      seconds += 1;
      for (var i = 0; i < milestones.length; i++) {
        var m = milestones[i];
        if (seconds === m && !fired[m]) {
          fired[m] = true;
          track('time_on_page', { quantity: m });
        }
      }
    }), 1000);
  }

  function hookClicks() {
    document.addEventListener('click', safe(function (e) {
      var el = e.target.closest && e.target.closest('a, button, [data-track]');
      if (!el) return;
      var href = el.getAttribute && el.getAttribute('href');
      if (href && href.indexOf('tel:') === 0) {
        track('phone_click', { search_query: href.slice(4) });
        return;
      }
      if (href && href.indexOf('mailto:') === 0) {
        track('email_click', { search_query: href.slice(7).split('?')[0] });
        return;
      }
      var label = el.getAttribute && el.getAttribute('data-track');
      if (!label) return;
      track('click', { search_query: label });
    }), true);
  }

  function hookFormFocus() {
    var seen = {};
    document.addEventListener('focusin', safe(function (e) {
      var form = e.target && e.target.closest && e.target.closest('form');
      if (!form) return;
      var id = form.id || form.getAttribute('action') || 'anonymous';
      if (seen[id]) return;
      seen[id] = true;
      track('form_focus', { search_query: id });
    }), true);
  }

  function hookVideo() {
    function attach(v) {
      if (v.__rc_hooked) return;
      v.__rc_hooked = true;
      v.addEventListener('play', safe(function () {
        track('video_play', { product_title: v.currentSrc || v.src || '' });
      }));
      v.addEventListener('pause', safe(function () {
        if (!v.ended) track('video_pause', { product_title: v.currentSrc || v.src || '' });
      }));
      v.addEventListener('ended', safe(function () {
        track('video_complete', { product_title: v.currentSrc || v.src || '' });
      }));
    }
    Array.prototype.forEach.call(document.querySelectorAll('video'), attach);
    if (window.MutationObserver) {
      var mo = new MutationObserver(safe(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var n = added[j];
            if (n && n.nodeType === 1) {
              if (n.tagName === 'VIDEO') attach(n);
              if (n.querySelectorAll) Array.prototype.forEach.call(n.querySelectorAll('video'), attach);
            }
          }
        }
      }));
      mo.observe(document.documentElement || document.body, { childList: true, subtree: true });
    }
  }

  function hookModals() {
    var seen = {};
    document.addEventListener('click', safe(function (e) {
      var trigger = e.target.closest && e.target.closest('[data-modal-target], [data-toggle="modal"]');
      if (!trigger) return;
      var target = trigger.getAttribute('data-modal-target') || trigger.getAttribute('data-target') || trigger.getAttribute('href') || '';
      if (seen[target]) return;
      seen[target] = true;
      track('modal_open', { search_query: target });
    }), true);
  }

  function hookGallery() {
    var lastFire = 0;
    document.addEventListener('flickity:select', safe(function () {
      var now = Date.now();
      if (now - lastFire < 1000) return;
      lastFire = now;
      track('gallery_swipe');
    }), true);
    document.addEventListener('click', safe(function (e) {
      var btn = e.target.closest && e.target.closest('.flickity-prev-next-button, [data-gallery-nav]');
      if (!btn) return;
      var now = Date.now();
      if (now - lastFire < 600) return;
      lastFire = now;
      track('gallery_swipe');
    }), true);
  }

  // Cart-attribute bridge: stuffs the cookie_id into Shopify cart.attributes
  // so the Custom Pixel running inside Shopify's hosted checkout can read it
  // (cross-domain — checkout.shopify.com cannot see our localStorage). The
  // attribute key is `_rc_cid`; the leading underscore hides it from email
  // notifications. Only fires once per page load when an add_to_cart happens.
  function bridgeCookieToCart() {
    if (window.__rc_cart_bridged) return;
    window.__rc_cart_bridged = true;
    try {
      var body = JSON.stringify({ attributes: { _rc_cid: getCookieID() } });
      fetch('/cart/update.js', {
        method: 'POST',
        keepalive: true,
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: body
      }).catch(function () {});
    } catch (_) {}
  }

  function boot() {
    bootPageView();
    hookCartAdd();
    hookScrollDepth();
    hookTimeOnPage();
    hookClicks();
    hookFormFocus();
    hookVideo();
    hookModals();
    hookGallery();
    // Always set the cart attribute on boot — survives across pages and
    // bridges cookie_id into the Shopify-hosted checkout via the Custom
    // Pixel. Idempotent: Shopify's /cart/update.js merges attributes.
    setTimeout(safe(bridgeCookieToCart), 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
